import React, { useEffect, useRef, useState } from 'react';
import Moveable from 'react-moveable';
import { FiX } from 'react-icons/fi';
import { StickerType } from '../hooks/useStickers';
import {
  SCPlacedStickerContainer,
  SCStickerImg,
  SCStickerDelete,
} from './styled';

interface StickerProps {
  sticker: StickerType;
  onUpdate: (id: string, updates: Partial<StickerType>) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string) => void;
  isSelected: boolean;
}

const Sticker = ({ sticker, onUpdate, onDelete, onSelect, isSelected }: StickerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const rotationRef = useRef<number>(sticker.rotation || 0);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      setTarget(ref.current);
    }
  }, []);

  const handleSelect = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    onSelect(sticker.id);
  };

  const handleDelete = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete(sticker.id);
  };

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: `${sticker.y}px`,
          left: `${sticker.x}px`,
          width: `${sticker.width || 100}px`,
          height: `${sticker.height || 100}px`,
          transform: `rotate(${sticker.rotation || 0}deg)`,
          zIndex: 10000,
        }}
        ref={ref}
        onMouseDown={handleSelect}
        onTouchStart={handleSelect}
      >
        <SCPlacedStickerContainer>
          <SCStickerImg crossOrigin="anonymous" src={sticker.url} alt="sticker" />
          {isSelected && (
            <SCStickerDelete onMouseDown={handleDelete} onTouchStart={handleDelete}>
              <FiX />
            </SCStickerDelete>
          )}
        </SCPlacedStickerContainer>
      </div>

      {isSelected && target && (
        <Moveable
          target={target}
          draggable={true}
          resizable={true}
          rotatable={true}
          keepRatio={true}
          throttleDrag={0}
          throttleResize={0}
          throttleRotate={0}
          renderDirections={["nw", "ne", "sw", "se"]}
          onDrag={({ target, left, top }) => {
            const container = ref.current?.parentElement;
            if (!container) return;

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;
            const bottomBarHeight = 150;
            const currentWidth = parseFloat(target.style.width) || sticker.width || 100;
            const currentHeight = parseFloat(target.style.height) || sticker.height || 100;

            const newX = Math.max(0, Math.min(left, containerWidth - currentWidth));
            const newY = Math.max(0, Math.min(top, containerHeight - bottomBarHeight - currentHeight));

            target.style.left = `${newX}px`;
            target.style.top = `${newY}px`;
          }}
          onDragEnd={({ target }) => {
            const newX = parseFloat(target.style.left);
            const newY = parseFloat(target.style.top);
            onUpdate(sticker.id, { x: newX, y: newY });
          }}
          onResize={({ target, width, height, drag }) => {
            const currentRotation = sticker.rotation || 0;
            target.style.width = `${width}px`;
            target.style.height = `${height}px`;
            target.style.transform = `${drag.transform} rotate(${currentRotation}deg)`;
          }}
          onResizeEnd={({ target }) => {
            const newWidth = parseFloat(target.style.width);
            const newHeight = parseFloat(target.style.height);
            const newX = parseFloat(target.style.left);
            const newY = parseFloat(target.style.top);
            const currentRotation = sticker.rotation || 0;

            target.style.transform = `rotate(${currentRotation}deg)`;

            onUpdate(sticker.id, {
              width: newWidth,
              height: newHeight,
              x: newX,
              y: newY,
            });
          }}
          onRotate={({ target, beforeRotate }) => {
            rotationRef.current = beforeRotate;
            target.style.transform = `rotate(${beforeRotate}deg)`;
          }}
          onRotateEnd={() => {
            onUpdate(sticker.id, { rotation: rotationRef.current });
          }}
        />
      )}
    </>
  );
};

export default Sticker;
